/**
 * Helpers for cancelled booking invoice / notification UI (Phase-2).
 */

export function getCancelledByLabel(cancelledBy) {
  if (cancelledBy === "customer_portal") {
    return "customer portal";
  }
  if (cancelledBy === "salon") {
    return "salon";
  }
  return "";
}

export function getBookingCanceledByText(cancelledBy) {
  const label = getCancelledByLabel(cancelledBy);
  return label ? `Booking Canceled by ${label}` : "";
}

export function resolveAppointmentCancelledBy(appointment) {
  if (!appointment) {
    return "";
  }
  if (appointment.cancelled_by) {
    return appointment.cancelled_by;
  }
  if (appointment.status === "Cancelled") {
    if (appointment.booking_type === "portal") {
      return "customer_portal";
    }
    if (appointment.cancellation_reason || appointment.cancellation_reason_id) {
      return "salon";
    }
  }
  return "";
}

/** Find the cancelled appointment row from sale invoice detail. */
export function resolveCancelledAppointment(saleData) {
  if (!saleData) {
    return null;
  }

  const appointments = saleData.appointments || [];
  if (Array.isArray(appointments) && appointments.length > 0) {
    const cancelled = appointments.find((item) => item && item.status === "Cancelled");
    if (cancelled) {
      return cancelled;
    }
  }

  if (saleData.status === "Cancelled") {
    return saleData;
  }

  const cart = saleData.cart || [];
  if (Array.isArray(cart)) {
    for (const item of cart) {
      if (item?.appointment?.status === "Cancelled") {
        return item.appointment;
      }
    }
  }

  return null;
}

/** Resolve cancelled_by from sale invoice detail (appointments / cart). */
export function resolveInvoiceCancelledBy(saleData) {
  const cancelledAppt = resolveCancelledAppointment(saleData);
  if (!cancelledAppt) {
    return "";
  }
  return resolveAppointmentCancelledBy(cancelledAppt);
}

/**
 * Full cancellation details for invoice / notification UI.
 * Always returns isCancelled when appointment status is Cancelled,
 * even if cancelled_by is missing.
 */
export function getInvoiceCancellationDetails(saleData) {
  const cancelledAppt = resolveCancelledAppointment(saleData);
  if (!cancelledAppt) {
    return {
      isCancelled: false,
      cancelledBy: "",
      canceledByText: "",
      statusLabel: "",
      canceledAt: "",
      cancellationReason: "",
    };
  }

  const cancelledBy = resolveAppointmentCancelledBy(cancelledAppt);
  const canceledByText = cancelledBy
    ? getBookingCanceledByText(cancelledBy)
    : "Booking Canceled";

  let canceledAt = "";
  const rawAt = cancelledAppt.is_status_at || cancelledAppt.updated_at || "";
  if (rawAt) {
    canceledAt = rawAt;
  }

  return {
    isCancelled: true,
    cancelledBy,
    canceledByText,
    statusLabel: "Cancelled",
    canceledAt,
    cancellationReason: cancelledAppt.cancellation_reason || "",
    appointment: cancelledAppt,
  };
}

export function isAppointmentCancelled(appointment) {
  return appointment && appointment.status === "Cancelled";
}

export function hasRefundPending(appointment) {
  if (!isAppointmentCancelled(appointment)) {
    return false;
  }
  if (typeof appointment.refund_pending === "boolean") {
    return appointment.refund_pending;
  }
  const payments = appointment.payment || [];
  if (!Array.isArray(payments) || payments.length === 0) {
    return false;
  }
  const salePaid = payments
    .filter((p) => p.type === "Sale")
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const depositPaid = payments
    .filter((p) => p.type === "Deposit")
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const depositRefund = payments
    .filter((p) => p.type === "DepositRefund")
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  return salePaid + depositPaid - depositRefund > 0;
}

export function getNotificationInvoiceNumber(appointment) {
  if (!appointment) {
    return "";
  }
  return (
    appointment.invoice_number ||
    appointment?.sale?.invoice_number ||
    ""
  );
}
